/* eslint-disable @typescript-eslint/no-explicit-any */
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {useEffect, useRef, VideoHTMLAttributes} from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';

const logger = new ConsoleLogger('VideoJS');

type SourceObject =
  | {
      src: string;
      type?: string;
    }
  | string;

interface VideoJSProps extends VideoHTMLAttributes<HTMLVideoElement> {
  options?: {
    autoplay?: boolean | 'play' | 'muted' | 'any';
    sources?: SourceObject[];
    html5: boolean;
    controls: boolean;
  };
  onReady?: (player: Player) => void;
  onAspectRatioDetected?: (aspect: '9/16' | '4/3' | 'inconnu', videoWidth: number, videoHeight: number) => void;
}

export const VideoJS = (props: VideoJSProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const isMountedRef = useRef(true);
  const {options, onReady, onAspectRatioDetected, ...rest} = props;

  const isApproximately = (value: number, target: number, tolerance = 0.01): boolean =>
    Math.abs(value - target) < tolerance;

  // Keep latest callbacks/options in refs so they don't retrigger the player
  // effect. The parent passes a fresh `options` object and inline callbacks on
  // every render; depending on them would re-run the effect each render and
  // re-call `player.src()`, which reloads the video and re-fires
  // `loadedmetadata` → onAspectRatioDetected → setState → infinite render loop.
  const onReadyRef = useRef(onReady);
  const onAspectRatioDetectedRef = useRef(onAspectRatioDetected);
  const optionsRef = useRef(options);
  useEffect(() => {
    onReadyRef.current = onReady;
    onAspectRatioDetectedRef.current = onAspectRatioDetected;
    optionsRef.current = options;
  });

  const autoplay = options?.autoplay;
  const firstSource = options?.sources && options.sources.length > 0 ? options.sources[0] : undefined;
  const currentSource = typeof firstSource === 'string' ? firstSource : firstSource?.src;
  const appliedSourceRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    isMountedRef.current = true;

    // Initialisation du player uniquement une fois
    if (!playerRef.current) {
      logger.debug('VideoJS: Initializing player');
      const videoElement = videoRef.current?.querySelector('video');
      if (!videoElement) {
        logger.error('VideoJS: Video element not found');
        return;
      }
      const player = (playerRef.current = videojs(videoElement, optionsRef.current, () => {
        if (!isMountedRef.current || player.isDisposed()) {
          return;
        }

        videojs.log('player is ready');
        onReadyRef.current?.(player);
      }));
      appliedSourceRef.current = currentSource;

      // Ajout de l'écouteur sur l'événement loadedmetadata
      player.on('loadedmetadata', () => {
        if (!isMountedRef.current || player.isDisposed()) {
          return;
        }

        const videoWidth = player.videoWidth();
        const videoHeight = player.videoHeight();

        if (!videoWidth || !videoHeight) {
          logger.error('VideoJS: Impossible de récupérer les dimensions de la vidéo.');
          return;
        }

        const ratio = videoWidth / videoHeight;
        let aspect: '9/16' | '4/3' | 'inconnu' = 'inconnu';

        if (isApproximately(ratio, 9 / 16)) {
          aspect = '9/16';
        } else if (isApproximately(ratio, 4 / 3)) {
          aspect = '4/3';
        }

        logger.debug(`VideoJS: Aspect ratio détecté : ${aspect} (dimensions : ${videoWidth}x${videoHeight})`);
        // Appel de la callback si elle est fournie
        onAspectRatioDetectedRef.current?.(aspect, videoWidth, videoHeight);
      });
    } else {
      const player = playerRef.current;

      if (player.isDisposed()) {
        return;
      }

      player.autoplay(autoplay ?? false);

      // Only reset the source when it actually changed: re-setting the same src
      // reloads the video and re-fires loadedmetadata, looping indefinitely.
      if (currentSource !== undefined && currentSource !== appliedSourceRef.current && optionsRef.current?.sources) {
        player.src(optionsRef.current.sources);
        appliedSourceRef.current = currentSource;
      }
    }
  }, [autoplay, currentSource]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      isMountedRef.current = false;

      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <div ref={videoRef} data-vjs-player>
      <video {...rest} />
    </div>
  );
};
